/* =========================================================
   WebsiteBuilder.tsx — FINAL, LENGKAP, STABIL
   - Struktur & data shape TIDAK DIUBAH
   - HANYA perbaikan logic agar SEMUA SECTION BERFUNGSI
   - SIMPAN tidak muter
   - INIT stabil
   - ESLint jsx-a11y/alt-text AMAN
========================================================= */

import React, { useState, useEffect } from 'react';
import { UserRole } from '../../types/db';
import type { LandingPageConfig, CMSSection } from '../../types/db';
import { uploadCMSMedia, saveCMSSection, fetchCMSSections } from '../../lib/cmsService';
import { supabase } from '../../lib/supabaseClient';
import { Save, Loader2, ShieldAlert, Plus, Trash2 } from 'lucide-react';

export const CMS_SECTION_TYPES = {
  BRANDING: 'branding',
  HERO: 'hero',
  YOUTUBE: 'youtube', // ✅ TAMBAH
  ABOUT: 'about',
  FEATURES: 'features',
  PRICING: 'pricing',
  PROMOS: 'promos',
  ACTIVITIES: 'activities',
  SUCCESS_STORIES: 'success_stories',
  CONNECT_WITH_US: 'connect_with_us',
} as const;

type CmsSectionType = (typeof CMS_SECTION_TYPES)[keyof typeof CMS_SECTION_TYPES];

export function WebsiteBuilder({
  config,
  onSave,
  role,
}: {
  config: LandingPageConfig;
  onSave: (c: LandingPageConfig) => Promise<void>;
  role: UserRole;
}) {
  /* ================= STATE ================= */
  const [formData, setFormData] = useState<any>({
    ...config,
    youtube_video_id: '',
    pricing: [],
    promos: [],
    activities: [],
    success_stories: [],
    connect_with_us: { youtube: '', instagram: '', tiktok: '', x: '' },
  });

  const [sections, setSections] = useState<Record<string, CMSSection>>({});
  const [activeTab, setActiveTab] = useState<CmsSectionType>('branding');
  const [pageId, setPageId] = useState<string | null>(null);
  const [isPagePublished, setIsPagePublished] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'IDLE' | 'SAVING' | 'SUCCESS'>('IDLE');

  /* ================= INIT DEFAULT SECTIONS ================= */
  const initFromConfig = async (pId: string) => {
    const defs: CMSSection[] = [
      {
        page_id: pId,
        type: 'branding',
        position: 0,
        is_active: true,
        content: { name: config.brand_name, motto: config.brand_motto, logo: config.logo_url },
      },
      {
        page_id: pId,
        type: 'hero',
        position: 1,
        is_active: true,
        content: {
          title: config.hero_title,
          subtitle: config.hero_subtitle,
          image: config.hero_image_url,
        },
      },

      // ✅ YOUTUBE DI POSISI 2
      { page_id: pId, type: 'youtube', position: 2, is_active: true, content: { video_id: '' } },

      {
        page_id: pId,
        type: 'about',
        position: 3,
        is_active: true,
        content: { title: config.about_title, description: config.about_description },
      },
      {
        page_id: pId,
        type: 'features',
        position: 4,
        is_active: true,
        content: { items: config.features || [] },
      },
      { page_id: pId, type: 'pricing', position: 5, is_active: true, content: { items: [] } },
      { page_id: pId, type: 'promos', position: 6, is_active: true, content: { items: [] } },
      { page_id: pId, type: 'activities', position: 7, is_active: true, content: { items: [] } },
      {
        page_id: pId,
        type: 'success_stories',
        position: 8,
        is_active: true,
        content: { items: [] },
      },
      {
        page_id: pId,
        type: 'connect_with_us',
        position: 9,
        is_active: true,
        content: { youtube: '', instagram: '', tiktok: '', x: '' },
      },
    ];

    const saved = await Promise.all(defs.map((d) => saveCMSSection(d)));
    const map: any = {};
    saved.forEach((s) => (map[s.type] = s));
    setSections(map);

    setFormData((prev: any) => ({
      ...prev,
      brand_name: config.brand_name,
      brand_motto: config.brand_motto,
      logo_url: config.logo_url,
      hero_title: config.hero_title,
      hero_subtitle: config.hero_subtitle,
      hero_image_url: config.hero_image_url,
      about_title: config.about_title,
      about_description: config.about_description,
      features: config.features || [],
      pricing: [],
      promos: [],
      activities: [],
      success_stories: [],
      connect_with_us: { youtube: '', instagram: '', tiktok: '', x: '' },
    }));
  };

  /* ================= LOAD FROM CMS ================= */
  const loadFromCMS = (data: CMSSection[]) => {
    const map: any = {};
    const next: any = {
      ...formData,
      pricing: [],
      promos: [],
      activities: [],
      success_stories: [],
      connect_with_us: { youtube: '', instagram: '', tiktok: '', x: '' },
    };

    data.forEach((s) => {
      map[s.type] = s;
      if (s.type === 'branding') {
        next.brand_name = s.content?.name || '';
        next.brand_motto = s.content?.motto || '';
        next.logo_url = s.content?.logo || '';
      } else if (s.type === 'youtube') {
        next.youtube_video_id = s.content?.video_id || '';
      } else if (s.type === 'hero') {
        next.hero_title = s.content?.title || '';
        next.hero_subtitle = s.content?.subtitle || '';
        next.hero_image_url = s.content?.image || '';
      } else if (s.type === 'about') {
        next.about_title = s.content?.title || '';
        next.about_description = s.content?.description || '';
      } else if (
        ['features', 'pricing', 'promos', 'activities', 'success_stories'].includes(s.type)
      ) {
        next[s.type] = s.content?.items || [];
      } else if (s.type === 'connect_with_us') {
        next.connect_with_us = {
          youtube: s.content?.youtube || '',
          instagram: s.content?.instagram || '',
          tiktok: s.content?.tiktok || '',
          x: s.content?.x || '',
        };
      }
    });

    setSections(map);
    setFormData(next);
  };

  /* ================= INIT CMS ================= */
  useEffect(() => {
    const run = async () => {
      setIsInitializing(true);
      let { data: page } = await supabase
        .from('cms_pages')
        .select('*')
        .eq('slug', 'home')
        .maybeSingle();
      if (!page) {
        const r = await supabase
          .from('cms_pages')
          .insert({ slug: 'home', title: 'Home', status: 'draft' })
          .select()
          .single();
        page = r.data;
      }
      setPageId(page.id);
      setIsPagePublished(page.status === 'published');

      const cms = await fetchCMSSections('home');
      if (!cms || cms.length === 0) {
        await initFromConfig(page.id);
      } else {
        const types = new Set(cms.map((c: any) => c.type));
        const needAll = Object.values(CMS_SECTION_TYPES).every((t) => types.has(t));
        if (!needAll) await initFromConfig(page.id);
        else loadFromCMS(cms);
      }
      setIsInitializing(false);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (role !== UserRole.MASTER_ADMIN) {
    return (
      <div className="h-[60vh] flex items-center justify-center">
        <ShieldAlert className="w-16 h-16 text-rose-500" />
      </div>
    );
  }

  /* ================= SAVE ================= */
  const handleSave = async () => {
    if (!pageId) return;
    setSaveStatus('SAVING');
    try {
      const payloads: CMSSection[] = [
        {
          id: sections.branding?.id,
          page_id: pageId,
          type: 'branding',
          position: 0,
          is_active: true,
          content: {
            name: formData.brand_name,
            motto: formData.brand_motto,
            logo: formData.logo_url,
          },
        },
        {
          id: sections.hero?.id,
          page_id: pageId,
          type: 'hero',
          position: 1,
          is_active: true,
          content: {
            title: formData.hero_title,
            subtitle: formData.hero_subtitle,
            image: formData.hero_image_url,
          },
        },
        {
          id: sections.youtube?.id,
          page_id: pageId,
          type: 'youtube',
          position: 2,
          is_active: true,
          content: { video_id: formData.youtube_video_id },
        },
        {
          id: sections.about?.id,
          page_id: pageId,
          type: 'about',
          position: 3,
          is_active: true,
          content: { title: formData.about_title, description: formData.about_description },
        },
        {
          id: sections.features?.id,
          page_id: pageId,
          type: 'features',
          position: 4,
          is_active: true,
          content: { items: formData.features },
        },
        {
          id: sections.pricing?.id,
          page_id: pageId,
          type: 'pricing',
          position: 5,
          is_active: true,
          content: { items: formData.pricing },
        },
        {
          id: sections.promos?.id,
          page_id: pageId,
          type: 'promos',
          position: 6,
          is_active: true,
          content: { items: formData.promos },
        },
        {
          id: sections.activities?.id,
          page_id: pageId,
          type: 'activities',
          position: 7,
          is_active: true,
          content: { items: formData.activities },
        },
        {
          id: sections.success_stories?.id,
          page_id: pageId,
          type: 'success_stories',
          position: 8,
          is_active: true,
          content: { items: formData.success_stories },
        },
        {
          id: sections.connect_with_us?.id,
          page_id: pageId,
          type: 'connect_with_us',
          position: 9,
          is_active: true,
          content: formData.connect_with_us,
        },
      ];
      await Promise.all(payloads.map((p) => saveCMSSection(p)));
      await onSave(formData);
      setSaveStatus('SUCCESS');
      setTimeout(() => setSaveStatus('IDLE'), 800);
    } catch {
      setSaveStatus('IDLE');
    }
  };

  const handlePublishToggle = async () => {
    if (!pageId) return;
    const newStatus = isPagePublished ? 'draft' : 'published';
    await supabase.from('cms_pages').update({ status: newStatus }).eq('id', pageId);
    setIsPagePublished(newStatus === 'published');
  };

  if (isInitializing) {
    return (
      <div className="h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin w-8 h-8 text-indigo-600" />
      </div>
    );
  }

  const tabs: CmsSectionType[] = [
    'branding',
    'hero',
    'youtube',
    'about',
    'features',
    'pricing',
    'promos',
    'activities',
    'success_stories',
    'connect_with_us',
  ];

  /* ================= UI ================= */
  return (
    <div className="p-8 space-y-6">
      {/* HEADER */}
      <div className="flex justify-between">
        <h1 className="text-2xl font-black">Website Builder</h1>
        <div className="flex gap-3">
          <button
            onClick={handlePublishToggle}
            className={`px-6 py-3 rounded-xl font-black ${isPagePublished ? 'bg-rose-500' : 'bg-emerald-500'} text-white`}
          >
            {isPagePublished ? 'TARIK DARI PUBLIK' : 'PUBLISH'}
          </button>
          <button
            onClick={handleSave}
            className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-black flex items-center gap-2"
          >
            {saveStatus === 'SAVING' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            SIMPAN
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-2 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`px-3 py-2 rounded-lg font-bold ${activeTab === t ? 'bg-indigo-600 text-white' : 'bg-slate-100'}`}
          >
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      {/* BRANDING */}
      {activeTab === 'branding' && (
        <div className="space-y-3">
          <input
            className="w-full p-3 border rounded"
            value={formData.brand_name}
            onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
          />
          <input
            className="w-full p-3 border rounded"
            value={formData.brand_motto}
            onChange={(e) => setFormData({ ...formData, brand_motto: e.target.value })}
          />
          {formData.logo_url && (
            <img src={formData.logo_url} alt="Brand logo" className="h-20 object-contain" />
          )}
          <input
            type="file"
            onChange={async (e) => {
              if (!e.target.files?.[0]) return;
              const url = await uploadCMSMedia(e.target.files[0]);
              setFormData({ ...formData, logo_url: url });
            }}
          />
        </div>
      )}

      {/* HERO */}
      {activeTab === 'hero' && (
        <div className="space-y-3">
          <input
            className="w-full p-3 border rounded"
            value={formData.hero_title}
            onChange={(e) => setFormData({ ...formData, hero_title: e.target.value })}
          />
          <textarea
            className="w-full p-3 border rounded h-32"
            value={formData.hero_subtitle}
            onChange={(e) => setFormData({ ...formData, hero_subtitle: e.target.value })}
          />
          {formData.hero_image_url && (
            <img
              src={formData.hero_image_url}
              alt="Hero section"
              className="w-full max-h-64 object-cover"
            />
          )}
          <input
            type="file"
            onChange={async (e) => {
              if (!e.target.files?.[0]) return;
              const url = await uploadCMSMedia(e.target.files[0]);
              setFormData({ ...formData, hero_image_url: url });
            }}
          />
        </div>
      )}
      {activeTab === 'youtube' && (
        <div className="space-y-3">
          <input
            className="w-full p-3 border rounded"
            placeholder="YouTube Video ID (contoh: dQw4w9WgXcQ)"
            value={formData.youtube_video_id}
            onChange={(e) => setFormData({ ...formData, youtube_video_id: e.target.value })}
          />
          {formData.youtube_video_id && (
            <iframe
              className="w-full aspect-video rounded-xl"
              src={`https://www.youtube.com/embed/${formData.youtube_video_id}`}
              title="YouTube video preview"
              allowFullScreen
            />
          )}
        </div>
      )}

      {/* ABOUT */}
      {activeTab === 'about' && (
        <div className="space-y-3">
          <input
            className="w-full p-3 border rounded"
            value={formData.about_title}
            onChange={(e) => setFormData({ ...formData, about_title: e.target.value })}
          />
          <textarea
            className="w-full p-3 border rounded h-40"
            value={formData.about_description}
            onChange={(e) => setFormData({ ...formData, about_description: e.target.value })}
          />
        </div>
      )}

      {/* FEATURES / PRICING / PROMOS / ACTIVITIES */}
      {['features', 'pricing', 'promos', 'activities'].includes(activeTab) && (
        <div className="space-y-3">
          {formData[activeTab].map((it: any, i: number) => (
            <div key={i} className="border p-3 rounded space-y-2">
              {activeTab === 'activities' && it.image && (
                <img src={it.image} alt="Activity" className="w-full max-h-48 object-cover" />
              )}
              {activeTab === 'activities' && (
                <input
                  type="file"
                  onChange={async (e) => {
                    if (!e.target.files?.[0]) return;
                    const url = await uploadCMSMedia(e.target.files[0]);
                    const n = [...formData.activities];
                    n[i] = { ...n[i], image: url };
                    setFormData({ ...formData, activities: n });
                  }}
                />
              )}
              <input
                className="w-full p-2 border rounded"
                value={it.title || ''}
                onChange={(e) => {
                  const n = [...formData[activeTab]];
                  n[i] = { ...n[i], title: e.target.value };
                  setFormData({ ...formData, [activeTab]: n });
                }}
              />
              <textarea
                className="w-full p-2 border rounded"
                value={it.description || ''}
                onChange={(e) => {
                  const n = [...formData[activeTab]];
                  n[i] = { ...n[i], description: e.target.value };
                  setFormData({ ...formData, [activeTab]: n });
                }}
              />
              <button
                onClick={() =>
                  setFormData({
                    ...formData,
                    [activeTab]: formData[activeTab].filter((_: any, idx: number) => idx !== i),
                  })
                }
              >
                <Trash2 />
              </button>
            </div>
          ))}
          <button
            onClick={() => setFormData({ ...formData, [activeTab]: [...formData[activeTab], {}] })}
          >
            <Plus /> tambah
          </button>
        </div>
      )}

      {/* SUCCESS STORIES */}
      {activeTab === 'success_stories' && (
        <div className="space-y-3">
          {formData.success_stories.map((it: any, i: number) => (
            <div key={i} className="border p-3 rounded space-y-2">
              {it.image && (
                <img src={it.image} alt="Success story" className="w-full max-h-48 object-cover" />
              )}
              <input
                type="file"
                onChange={async (e) => {
                  if (!e.target.files?.[0]) return;
                  const url = await uploadCMSMedia(e.target.files[0]);
                  const n = [...formData.success_stories];
                  n[i] = { ...n[i], image: url };
                  setFormData({ ...formData, success_stories: n });
                }}
              />
              <button
                onClick={() =>
                  setFormData({
                    ...formData,
                    success_stories: formData.success_stories.filter(
                      (_: any, idx: number) => idx !== i
                    ),
                  })
                }
              >
                <Trash2 />
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              setFormData({ ...formData, success_stories: [...formData.success_stories, {}] })
            }
          >
            <Plus /> tambah
          </button>
        </div>
      )}

      {/* CONNECT */}
      {activeTab === 'connect_with_us' && (
        <div className="space-y-3">
          {['youtube', 'instagram', 'tiktok', 'x'].map((k) => (
            <input
              key={k}
              className="w-full p-3 border rounded"
              placeholder={k}
              value={formData.connect_with_us[k] || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  connect_with_us: { ...formData.connect_with_us, [k]: e.target.value },
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
